import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSliderDragEvent } from '@angular/material/slider';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { onlyInLeft } from 'PreciateUtil';
import { combineLatest, firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { RoomIdentifierType } from 'app/modules/social/alternate-hosts-selector/alternate-hosts-selector.component';
import { MiniMapState } from 'app/modules/social/social-mini-map/mini-map-state';
import {
  SocialSetSettingsType,
  SocialSettings,
  SocialSettingsType,
} from 'app/modules/social/social-settings/social-settings.type';
import { socialHomePageActions } from 'app/modules/social/state/home-page';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import AlternateHost from 'entity/AlternateHost';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import {
  ANYONE_CAN_CHANGE_MUSIC,
  ANYONE_CAN_PRESENT,
  ANYONE_CAN_SHARE,
  ANYONE_CAN_SHARE_TO_GROUP,
  CONVO,
  HOST_CHAT,
  MODERATED_CHAT,
  PHOTO_BOOTH,
  SHARE_LINK,
  TOAST,
  SAG_ENABLED,
} from 'entity/SocialSettings';
import User from 'entity/User';
import SocialPermissionType from 'enums/SocialPermissionType';

import { ScootaverseEditService } from '../services/scootaverse-edit.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { ScootaversePageService } from '../services/scootaverse-page.service';
import { GroupManager } from 'app/modules/social/social-pixi-canvas/group-manager';

const MENU_OPTIONS = {
  ROOM: 'Room Settings',
  MAP: 'Map Settings',
  JOIN: 'Join Settings',
  HOSTS: 'Alternate Hosts',
};

// optionals properties are defined by a feature flag
interface SocialSettingsFormGroup {
  canToast: [boolean];
  canConvo: [boolean];
  canShareLink: [boolean];
  canStartPhotoBooth: [boolean];
  anyoneCanPresent: [boolean];
  anyoneCanShare: [boolean];
  anyoneCanShareToGroup: [boolean];
  anyoneCanChangeMusic: [boolean];
  socialPermissionType: [SocialPermissionType];
  anyoneCanStart: [boolean | undefined];
  hostAdmitted: [boolean | undefined];
  recordMeeting: [boolean];
  canHostChat?: [boolean];
  canChatModeration?: [boolean];
  aiSummaries?: [boolean];
  specialAccessGroup?: [boolean];
}

@Component({
  selector: 'app-scootaverse-settings-page',
  templateUrl: './scootaverse-settings-page.component.html',
  styleUrl: './scootaverse-settings-page.component.scss',
  standalone: false,
})
export class ScootaverseSettingsPageComponent extends Loggable implements OnInit, OnDestroy {
  // loaders
  protected isLoading = true;
  protected isSaving = false;

  // form
  public socialSettingsFormGroup?: FormGroup;
  protected menuOptions = MENU_OPTIONS;
  public sideMenuOptions = [
    { text: MENU_OPTIONS.ROOM, isActive: true },
    { text: MENU_OPTIONS.MAP, isActive: false },
    { text: MENU_OPTIONS.JOIN, isActive: false },
    { text: MENU_OPTIONS.HOSTS, isActive: false },
  ];
  public minimapModes = [
    { value: MiniMapState.CLOSED, label: 'Closed' },
    { value: MiniMapState.OPENED, label: 'Opened' },
    { value: MiniMapState.EXPANDED, label: 'Expanded' },
  ];
  public socialIsOpen = false;
  public groupSoftCap = false;
  public groupSizeEnabled = false;
  public groupSize = 10;
  public socialRoom?: Social;
  public socialRoomSet?: SocialRoomSet;
  public socialPermissionTypeEnum = SocialPermissionType;
  public alternateHostsIdentifier = RoomIdentifierType.SOCIAL_ROOM_SET_ID;
  public showHostLicenseError = false;

  private currentAlternateHosts: User[] = [];
  private selectedAlternateHosts: User[] = [];

  public minimapMode = MiniMapState.EXPANDED;
  private destroyed$ = new ReplaySubject<boolean>(1);

  protected moderatedChat = false;
  protected aiSummaries = false;

  protected specialAccessGroup = false;

  public constructor(
    private fb: FormBuilder,
    private store: Store,
    private route: ActivatedRoute,
    private _toasterService: ToastrService,
    private scootaverseEditService: ScootaverseEditService,
    private videoChatService: VideoChatService,
    private scootaversePageServive: ScootaversePageService,
  ) {
    super();

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.moderated_chat),
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.ai_summaries),
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.special_access_groups),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async ([moderatedChat, aiSummaries, specialAccessGroup]) => {
        // TODO: this prevents a race condition, refactor to reference observable
        await this.getRoomSettings();
        this.moderatedChat = moderatedChat;
        this.aiSummaries = aiSummaries;
        this.specialAccessGroup = specialAccessGroup;

        const formGroup: SocialSettingsFormGroup = {
          canToast: [false],
          canConvo: [false],
          canShareLink: [false],
          canStartPhotoBooth: [false],
          anyoneCanPresent: [false],
          anyoneCanShare: [false],
          anyoneCanShareToGroup: [false],
          anyoneCanChangeMusic: [false],
          socialPermissionType: [SocialPermissionType.PUBLIC],
          anyoneCanStart: [this.socialRoomSet?.anyoneCanStart],
          hostAdmitted: [this.socialRoomSet?.hostAdmitted],
          recordMeeting: [false],
        };

        if (moderatedChat) {
          formGroup.canHostChat = [true];
          formGroup.canChatModeration = [false];
        }

        if (aiSummaries) {
          formGroup.aiSummaries = [false];
        }

        if (specialAccessGroup) {
          formGroup.specialAccessGroup = [false];
        }

        this.socialSettingsFormGroup = this.fb.group(formGroup);
      });

    getFeatureFlagObservable(store, LaunchDarklyFlags.group_soft_cap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSoftCap) => {
        this.groupSoftCap = groupSoftCap;
      });
  }

  public async ngOnInit(): Promise<void> {
    await this.getRoomSettings();
  }

  private async getRoomSettings() {
    const roomSetId = this.route.snapshot.paramMap.get('id');
    if (!roomSetId) {
      this.error('No RoomSetId was informed');
      return;
    }

    try {
      this.socialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(roomSetId));
      if (!this.socialRoomSet || !this.socialRoomSet.socials || this.socialRoomSet.socials.length === 0) {
        this.error('No SocialRoomSet was found');
        return;
      }
      this.socialRoom = await firstValueFrom(
        this.videoChatService.getSocial(this.socialRoomSet.socials[0].socialId ?? ''),
      );
      const socialSettings: SocialSettings = await this.scootaverseEditService.getRoomSettings(
        this.socialRoom.socialId ?? '',
      );
      const roomSetSettings = await firstValueFrom(this.videoChatService.getSocialRoomSetSettings(roomSetId));
      // during migration, some roomSetSettings may not exist
      this.mapSettingsToForm({ ...socialSettings.settings, ...roomSetSettings.setSettings });
    } catch (err) {
      this.error('Error while fetching getSocialRoomSetSettings()', err);
    } finally {
      this.isLoading = false;
    }
  }

  private mapSettingsToForm(setSettings?: Partial<SocialSetSettingsType>) {
    if (!setSettings) {
      this.error('Could not map SocialSetSettingsType');
      return;
    }

    const mappings = [
      { formKey: 'canToast', settingKey: TOAST },
      { formKey: 'canConvo', settingKey: CONVO },
      { formKey: 'canShareLink', settingKey: SHARE_LINK },
      { formKey: 'canStartPhotoBooth', settingKey: PHOTO_BOOTH },
      { formKey: 'anyoneCanPresent', settingKey: ANYONE_CAN_PRESENT },
      { formKey: 'anyoneCanShare', settingKey: ANYONE_CAN_SHARE },
      { formKey: 'anyoneCanShareToGroup', settingKey: ANYONE_CAN_SHARE_TO_GROUP },
      { formKey: 'anyoneCanChangeMusic', settingKey: ANYONE_CAN_CHANGE_MUSIC },
      { formKey: 'recordMeeting', settingKey: 'RECORD' },
    ];

    if (this.moderatedChat) {
      mappings.push(
        { formKey: 'canHostChat', settingKey: HOST_CHAT },
        { formKey: 'canChatModeration', settingKey: MODERATED_CHAT },
      );
    }

    if (this.aiSummaries) {
      mappings.push({ formKey: 'aiSummaries', settingKey: 'AI_SUMMARIES' });
    }

    if (this.specialAccessGroup) {
      mappings.push({ formKey: 'specialAccessGroup', settingKey: SAG_ENABLED });
    }

    mappings.forEach((map) => {
      const formKey = map.formKey;
      const settingKey = map.settingKey;
      if (setSettings && setSettings[settingKey]) {
        this.socialSettingsFormGroup?.get(formKey)?.setValue(setSettings[settingKey]);
      }
    });

    if (setSettings.MAX_GROUP_SIZE) {
      this.groupSize = GroupManager.getConfiguredGroupSize(this.groupSoftCap, setSettings.MAX_GROUP_SIZE);
    }

    if (setSettings.MINIMAP_MODE) {
      this.minimapMode = setSettings.MINIMAP_MODE as number;
    }
    this.socialSettingsFormGroup?.get('anyoneCanStart')?.setValue(this.socialRoomSet?.anyoneCanStart);
    this.socialSettingsFormGroup?.get('hostAdmitted')?.setValue(this.socialRoomSet?.hostAdmitted);
    this.socialSettingsFormGroup?.get('socialPermissionType')?.setValue(this.socialRoomSet?.socialPermissionType);

    this.selectedAlternateHosts = this.socialRoomSet?.alternateHosts ?? [];
    this.currentAlternateHosts = this.socialRoomSet?.alternateHosts ?? [];
  }

  protected changeMenu(option: string) {
    this.sideMenuOptions.forEach((item) => (item.isActive = item.text === option));
  }

  public minimapModeChange(mode: MatRadioChange): void {
    this.minimapMode = mode.value;
  }

  public onGroupSizeChange(event: MatSliderDragEvent): void {
    this.groupSize = Number(event.value);
  }

  public changeTogleValue($event: MatSlideToggleChange, field: string): void {
    if ($event.checked) {
      this.socialSettingsFormGroup?.get(field)?.setValue(false);
    } else {
      this.socialSettingsFormGroup?.get(field)?.setValue(true);
    }
  }

  public onAlternateHostsChange(alternateHosts: AlternateHost[]): void {
    this.showHostLicenseError = alternateHosts.some((a) => !a.licensed);
    this.selectedAlternateHosts = alternateHosts.map((a) => a.user).filter(notNil);
  }

  public save(): void {
    const roomSetId = this.route.snapshot.paramMap.get('id');
    if (!roomSetId) {
      this.error('No RoomSetId was informed');
      return;
    }

    try {
      this.isSaving = true;

      // Update Room Entity
      this.updateRoomEntity(roomSetId);

      // Update Social Settings
      this.updateSocialSettings(roomSetId);

      // Update Alternate Hosts
      this.updateAlternateHosts(roomSetId);

      this._toasterService.success('Room settings saved successfully!', 'Success');
    } catch (err) {
      this.error('savePermissions error', err);
    } finally {
      this.isSaving = false;
    }
  }

  private updateRoomEntity(roomSetId: string): void {
    const room = {
      socialRoomSetId: roomSetId,
      anyoneCanStart: this.socialSettingsFormGroup?.get('anyoneCanStart')?.value,
      hostAdmitted: this.socialSettingsFormGroup?.get('hostAdmitted')?.value,
      socialPermissionType: this.socialSettingsFormGroup?.get('socialPermissionType')?.value,
    };
    this.scootaversePageServive.updateSocialRoomSet(room).catch((err) => this.error(err));
  }

  private updateSocialSettings(roomSetId: string): void {
    let setSettings: SocialSettingsType;

    setSettings = {
      TOAST: this.socialSettingsFormGroup?.get('canToast')?.value,
      CONVO: this.socialSettingsFormGroup?.get('canConvo')?.value,
      SHARE_LINK: this.socialSettingsFormGroup?.get('canShareLink')?.value,
      PHOTO_BOOTH: this.socialSettingsFormGroup?.get('canStartPhotoBooth')?.value,
      ANYONE_CAN_PRESENT: this.socialSettingsFormGroup?.get('anyoneCanPresent')?.value,
      ANYONE_CAN_SHARE: this.socialSettingsFormGroup?.get('anyoneCanShare')?.value,
      ANYONE_CAN_SHARE_TO_GROUP: this.socialSettingsFormGroup?.get('anyoneCanShareToGroup')?.value,
      ANYONE_CAN_CHANGE_MUSIC: this.socialSettingsFormGroup?.get('anyoneCanChangeMusic')?.value,
      MINIMAP_MODE: this.minimapMode,
      MAX_GROUP_SIZE: GroupManager.getAdjustedGroupSize(this.groupSoftCap, this.groupSize),
      RECORD: this.socialSettingsFormGroup?.get('recordMeeting')?.value,
    };

    if (this.moderatedChat) {
      setSettings = {
        ...setSettings,
        HOST_CHAT: this.socialSettingsFormGroup?.get('canHostChat')?.value,
        MODERATED_CHAT: this.socialSettingsFormGroup?.get('canChatModeration')?.value,
      };
    }

    if (this.aiSummaries) {
      setSettings = {
        ...setSettings,
        AI_SUMMARIES: this.socialSettingsFormGroup?.get('aiSummaries')?.value,
      };
    }

    if (this.specialAccessGroup) {
      setSettings = {
        ...setSettings,
        SAG_ENABLED: this.socialSettingsFormGroup?.get('specialAccessGroup')?.value,
      };
    }

    const settings: SocialSettings = { setSettings: setSettings };

    this.store.dispatch(socialHomePageActions.SAVE_ROOMSET_SETTINGS_REQ({ settings: settings, roomSetId: roomSetId }));
  }
  private updateAlternateHosts(roomSetId: string): void {
    const removedHosts = onlyInLeft(
      this.currentAlternateHosts,
      this.selectedAlternateHosts,
      (a, b) => a.userId === b.userId,
    );
    const addedHosts = onlyInLeft(
      this.selectedAlternateHosts,
      this.currentAlternateHosts,
      (a, b) => a.userId === b.userId,
    );
    const alternateHosts: AlternateHost[] = [
      ...removedHosts.map((h) => ({
        user: h,
        host: false,
      })),
      ...addedHosts.map((h) => ({
        user: h,
        host: true,
      })),
    ];
    this.store.dispatch(
      socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_ROOMSET_REQ({
        socialRoomSetId: roomSetId,
        alternateHosts,
      }),
    );
  }

  public get activeMenuItem() {
    return this.sideMenuOptions.find((item) => item.isActive);
  }

  public get isPermissionTypePublic() {
    if (!this.socialSettingsFormGroup) {
      return false;
    }
    return this.socialSettingsFormGroup.get('socialPermissionType')?.value === SocialPermissionType.PUBLIC;
  }

  protected toggleAiSummaries(e: MatSlideToggleChange, formKey: 'recordMeeting' | 'aiSummaries'): void {
    if (formKey === 'aiSummaries' && e.checked && this.aiSummaries) {
      this.socialSettingsFormGroup?.get('aiSummaries')?.setValue(true);
      this.socialSettingsFormGroup?.get('recordMeeting')?.setValue(true);
    }

    if (formKey === 'recordMeeting' && !e.checked && this.aiSummaries) {
      this.socialSettingsFormGroup?.get('aiSummaries')?.setValue(false);
      this.socialSettingsFormGroup?.get('recordMeeting')?.setValue(false);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
